import { useMemo, useRef, useState } from 'react';
import { Icon } from '../components/ui/Icon';
import { IconButton, Segmented, Sheet, Switch } from '../components/ui/ui';
import { addCustomFontFromFile } from '../designs/customFonts';
import { allFonts, fontStack } from '../designs/fonts';
import { resolveLook } from '../designs/look';
import { uid } from '../lib/id';
import { store, useStore } from '../state/store';
import type { CompletedBehavior, LineMarks, Note, NoteStyle, Pattern } from '../storage/schemas';
import { FIT_MIN } from './useAutoFit';
import type { NoteEditing } from './useNoteEditing';

type Tab = 'font' | 'style' | 'colour';

const SWATCHES = ['#000000', '#FFFFFF', '#F1D100', '#968100', '#E15651', '#FF6120', '#F5879E', '#7752DD', '#475ADC', '#367ED4', '#43BDAE', '#41D34A', '#2B6153', '#5D4C44', '#EDE7D6', '#B3B3B3'];

function ColorRow({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const id = useMemo(() => uid('c'), []);
  return (
    <div className="ds-color-row">
      <label htmlFor={id}>{label}</label>
      <div className="ds-swatches">
        {SWATCHES.map((c) => (
          <button key={c} type="button" className={`ds-swatch ${value.toUpperCase() === c ? 'is-on' : ''}`} style={{ background: c }} aria-label={`${label} ${c}`} aria-pressed={value.toUpperCase() === c} onClick={() => onChange(c)} />
        ))}
        <input id={id} type="color" value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} aria-label={`Custom ${label.toLowerCase()} colour`} />
      </div>
    </div>
  );
}

/** "Aa": fonts and text styling for the current note. */
export function TextSheet({ open, onClose, note, editing, focusedLineId }: { open: boolean; onClose: () => void; note: Note; editing: NoteEditing; focusedLineId: string | null }) {
  const [tab, setTab] = useState<Tab>('font');
  const designs = useStore((s) => s.designs);
  const customFonts = useStore((s) => s.settings.customFonts);
  const look = resolveLook(note, designs);
  const fileRef = useRef<HTMLInputElement>(null);
  const setStyle = (patch: Partial<NoteStyle>, group = 'style') => editing.edit((n) => ({ ...n, style: { ...n.style, ...patch } }), group);
  const marks = note.lines.find((l) => l.id === focusedLineId)?.marks ?? {};
  const toggleMark = (k: keyof LineMarks) => {
    if (!focusedLineId) return;
    editing.edit((n) => ({ ...n, lines: n.lines.map((l) => (l.id === focusedLineId ? { ...l, marks: { ...l.marks, [k]: !l.marks?.[k] } } : l)) }), 'marks');
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Text"
      hideTitle
      className="design-sheet"
      header={
        <Segmented<Tab>
          label="Text options"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'font', label: 'Font' },
            { value: 'style', label: 'Text' },
            { value: 'colour', label: 'Colours' },
          ]}
        />
      }
    >
      {tab === 'font' && (
        <div className="ds-pane">
          <ul className="font-list" aria-label="Fonts">
            {allFonts().map((f) => {
              const on = look.font === f.id;
              return (
                <li key={f.id}>
                  <button type="button" className={`font-row ${on ? 'is-on' : ''}`} aria-pressed={on} onClick={() => setStyle({ font: f.id }, 'font')}>
                    <span className="font-sample" style={{ fontFamily: fontStack(f.id) }}>
                      Pick up milk
                    </span>
                    <span className="font-name">
                      {f.label}
                      {f.group !== 'Handwriting' && <small> · {f.group}</small>}
                    </span>
                    {on && <Icon name="check" size={20} />}
                  </button>
                </li>
              );
            })}
          </ul>
          <input
            ref={fileRef}
            type="file"
            accept=".ttf,.otf,.woff,.woff2,font/ttf,font/otf,font/woff,font/woff2"
            hidden
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              try {
                const def = await addCustomFontFromFile(file);
                setStyle({ font: def.id }, 'font');
                store.toast(`Added font “${def.label}”.`, 'success');
              } catch (err) {
                store.toast((err as Error).message, 'error');
              }
            }}
          />
          <button type="button" className="btn ds-add-font" onClick={() => fileRef.current?.click()}>
            Add your own font file…
          </button>
          {customFonts.length > 0 && <p className="ds-note">Custom fonts are stored on this device only.</p>}
        </div>
      )}

      {tab === 'style' && (
        <div className="ds-pane ds-style">
          <div className="ds-field">
            <span className="ds-label">Text size</span>
            <div className="ds-inline">
              <label className="ds-inline">
                Auto-fit
                <Switch label="Shrink text to fit" checked={note.style.autoSize} onChange={(v) => setStyle({ autoSize: v })} />
              </label>
              <input
                type="range"
                min={FIT_MIN}
                max={12}
                step={0.1}
                value={note.style.fontScale}
                disabled={note.style.autoSize}
                aria-label="Text size"
                onChange={(e) => setStyle({ fontScale: Number(e.target.value), autoSize: false }, 'size')}
              />
            </div>
          </div>
          <div className="ds-field">
            <span className="ds-label">Alignment</span>
            <div className="ds-inline">
              <button type="button" className={`chip ${note.style.align === 'block' ? 'is-on' : ''}`} aria-pressed={note.style.align === 'block'} onClick={() => setStyle({ align: 'block' })}>
                Centred block
              </button>
              {(['left', 'center', 'right'] as const).map((a) => (
                <IconButton key={a} icon={`align-${a}`} label={`Align ${a}`} pressed={note.style.align === a} onClick={() => setStyle({ align: a })} variant="circle" />
              ))}
              <button type="button" className={`chip ${note.style.vAlign === 'top' ? 'is-on' : ''}`} aria-pressed={note.style.vAlign === 'top'} onClick={() => setStyle({ vAlign: note.style.vAlign === 'top' ? 'center' : 'top' })}>
                Start at top
              </button>
            </div>
          </div>
          <div className="ds-field">
            <span className="ds-label">Line spacing</span>
            <input type="range" min={0.9} max={2.2} step={0.02} value={note.style.lineHeight} aria-label="Line spacing" onChange={(e) => setStyle({ lineHeight: Number(e.target.value) }, 'lh')} />
          </div>
          <div className="ds-field">
            <span className="ds-label">Current line {focusedLineId ? '' : '(tap a line first)'}</span>
            <div className="ds-inline">
              <IconButton icon="heading" label="Heading" pressed={!!marks.heading} disabled={!focusedLineId} onClick={() => toggleMark('heading')} variant="circle" />
              <IconButton icon="bold" label="Bold" pressed={!!marks.bold} disabled={!focusedLineId} onClick={() => toggleMark('bold')} variant="circle" />
              <IconButton icon="italic" label="Italic" pressed={!!marks.italic} disabled={!focusedLineId} onClick={() => toggleMark('italic')} variant="circle" />
              <IconButton icon="underline" label="Underline" pressed={!!marks.underline} disabled={!focusedLineId} onClick={() => toggleMark('underline')} variant="circle" />
            </div>
          </div>
          <div className="ds-field">
            <span className="ds-label">Completed items</span>
            <Segmented<CompletedBehavior>
              label="Completed items"
              value={note.style.completed}
              onChange={(v) => setStyle({ completed: v })}
              options={[
                { value: 'strike', label: 'Cross out' },
                { value: 'bottom', label: 'Move down' },
                { value: 'hide', label: 'Hide' },
              ]}
            />
          </div>
        </div>
      )}

      {tab === 'colour' && (
        <div className="ds-pane ds-style">
          <p className="ds-note">Changes apply to this note only. Pick a design to start over.</p>
          <ColorRow label="Paper" value={look.paper} onChange={(v) => setStyle({ paper: v }, 'paper')} />
          <ColorRow label="Text" value={look.ink} onChange={(v) => setStyle({ ink: v, accent: v }, 'ink')} />
          <ColorRow label="Lines" value={look.line} onChange={(v) => setStyle({ line: v }, 'line')} />
          <div className="ds-field">
            <span className="ds-label">Paper style</span>
            <Segmented<Pattern>
              label="Paper style"
              value={look.pattern}
              onChange={(v) => setStyle({ pattern: v }, 'pattern')}
              options={[
                { value: 'grid', label: 'Grid' },
                { value: 'ruled', label: 'Lined' },
                { value: 'dots', label: 'Dots' },
                { value: 'plain', label: 'Plain' },
              ]}
            />
          </div>
        </div>
      )}
    </Sheet>
  );
}
